// @ts-strict-ignore
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Avatar from 'components/avatar';
import { BaseButtonType } from 'components/base-button';
import Divider from 'components/divider';
import LinkButton from 'components/link-button';
import { Heading, Text } from 'components/typography';

import TalkTypes from '../talk-types';
import TalkTypeDescriptionProps from './TalkTypeDescription.props';

const TalkTypeDescription = ({ type, id }: TalkTypeDescriptionProps) => {
  const [customContentPartner, setCustomContentPartner] = useState<{
    partnerName: string;
    blurb: string;
    avatar: string;
  }>({
    partnerName: '',
    blurb: '',
    avatar: ''
  });

  const getCustomContentPartner = async (id: string) => {
    const customContentMap = await import('../custom-content-map');
    const customContentPartner = {
      partnerName: customContentMap.default[id]?.partnerName || '',
      blurb: customContentMap.default[id]?.partnerDescription || '',
      avatar: customContentMap.default[id]?.partnerAvatarUrl || ''
    };

    return customContentPartner;
  };

  useEffect(() => {
    if (type.id === TalkTypes.CustomSponsoredContent) {
      (async () => {
        const customContentPartner = await getCustomContentPartner(id);
        setCustomContentPartner(customContentPartner);
      })();
    }
  }, [type, id]);

  const eventTypeMap = {
    [TalkTypes.TedXTalk]: {
      name: 'TEDx',
      blurb:
        'TEDx was created in the spirit of TED\'s mission, "ideas worth spreading." It supports independent organizers who want to create a TED-like event in their own community.',
      link: {
        url: '/about/programs-initiatives/tedx-program',
        text: 'Find a TEDx event near you'
      }
    },
    [TalkTypes.TedEdOriginal]: {
      name: 'TED-Ed',
      blurb:
        'TED-Ed Original lessons feature the words and ideas of educators brought to life by professional animators.',
      link: {
        url: 'https://ed.ted.com/',
        text: 'Learn more about TED-Ed'
      }
    },
    [TalkTypes.TedInstituteTalk]: {
      name: 'TED Institute',
      blurb:
        'Every year, TED works with a group of select companies and foundations to identify internal ideators, inventors, connectors, and creators. Drawing on the same rigorous regimen that has prepared speakers for the TED main stage, TED Institute works closely with each partner, overseeing curation and providing intensive one-on-one talk development to sharpen and fine tune ideas. The culmination is an event produced, recorded, and hosted by TED, generating a growing library of valuable TED Talks that can spur innovation and transform organizations.',
      link: {
        url: '/about/programs-initiatives/ted-institute',
        text: 'Read more about the TED Institute'
      }
    },
    [TalkTypes.BestOfWeb]: {
      name: 'Best of the Web',
      blurb:
        "Best of the Web, like the rest of TED.com, involves compelling people and ideas. But unlike TED Talks, these videos don't come from TED or our partner conferences: they're curated from around the internet, drawing on freely available videos from creators with an idea worth spreading."
    },
    [TalkTypes.TedSalonTalk]: {
      name: 'TED Salon',
      blurb:
        'TED Salons welcome an intimate audience for an afternoon or evening of highly-curated TED Talks revolving around a globally relevant theme. A condensed version of a TED flagship conference, they are distinct in their brevity, opportunities for conversation, and heightened interaction between the speaker and audience.',
      link: {
        url: '/attend/conferences/special-events/ted-salons',
        text: 'Read more about TED Salons'
      }
    },
    [TalkTypes.CustomSponsoredContent]: {
      name: 'the partner',
      ...customContentPartner
    }
  };

  if (!eventTypeMap[type.id]) {
    return null;
  }

  return (
    <div className="mb-12">
      <Heading size="m" tagName="h2">
        <FormattedMessage
          defaultMessage="About {name}"
          values={{ name: eventTypeMap[type.id].name }}
        />
        <Divider className="mb-4 mt-1" />
      </Heading>
      <div className="flex flex-col">
        <div className="flex min-w-fit items-center">
          {customContentPartner.avatar && (
            <Avatar
              src={customContentPartner.avatar}
              alt={customContentPartner.partnerName}
              className="h-14 w-14 rounded-full"
              hasBackgroundColor={false}
              imgWidth={56}
            />
          )}
          <Text size="l" className="mb-1 ml-2 font-bold">
            {customContentPartner.partnerName}
          </Text>
        </div>

        <div className="lg:max-w- flex flex-col lg:ml-2">
          <Text size="m" className="mb-1 mt-3">
            <FormattedMessage
              defaultMessage="{blurb}"
              values={{ blurb: eventTypeMap[type.id].blurb }}
            />
          </Text>
        </div>
      </div>

      {eventTypeMap[type.id].link && (
        <div className="mt-8 flex justify-end">
          <LinkButton
            small
            variant={BaseButtonType.Tertiary}
            href={eventTypeMap[type.id].link.url}
            text={eventTypeMap[type.id].link.text}
          />
        </div>
      )}
    </div>
  );
};

export default TalkTypeDescription;
